import { buildModelIdentityIndex, isGeneratedModelDescription, modelPathSegmentToSlug, modelVariantTags, } from './modelIdentity.js';
import { withResidencyBoundary } from './residencyBoundary.js';
const TASK_MODALITIES = {
    'Text Generation': { input: ['TEXT'], output: ['TEXT'] },
    'Text Embeddings': { input: ['TEXT'], output: ['EMBEDDING'] },
    'Text Classification': { input: ['TEXT'], output: ['TEXT'] },
    Translation: { input: ['TEXT'], output: ['TEXT'] },
    Summarization: { input: ['TEXT'], output: ['TEXT'] },
    'Text-to-Image': { input: ['TEXT'], output: ['IMAGE'] },
    'Image-to-Text': { input: ['IMAGE'], output: ['TEXT'] },
    'Image Classification': { input: ['IMAGE'], output: ['TEXT'] },
    'Object Detection': { input: ['IMAGE'], output: ['TEXT'] },
    'Text-to-Speech': { input: ['TEXT'], output: ['AUDIO'] },
    'Automatic Speech Recognition': { input: ['AUDIO'], output: ['TEXT'] },
};
function truthy(value) {
    return value === true || value === 'true';
}
function providerProperties(offering) {
    const properties = offering.providerSpecific?.properties;
    return properties && typeof properties === 'object' ? properties : {};
}
/**
 * Capabilities for an offering. Catalogues such as Workers AI publish capability
 * properties (context window, vision, task) instead of a capabilities block.
 */
export function getOfferingCapabilities(offering) {
    const caps = { ...(offering.capabilities ?? {}) };
    const properties = providerProperties(offering);
    const task = offering.providerSpecific?.task;
    const taskModalities = typeof task === 'string' ? TASK_MODALITIES[task] : undefined;
    const contextWindow = Number(properties.context_window);
    if (!caps.contextWindowTokens && Number.isFinite(contextWindow) && contextWindow > 0) {
        caps.contextWindowTokens = contextWindow;
    }
    if (!caps.inputModalities?.length && taskModalities) {
        caps.inputModalities = [...taskModalities.input];
    }
    if (!caps.outputModalities?.length && taskModalities) {
        caps.outputModalities = [...taskModalities.output];
    }
    if (truthy(properties.vision) && !caps.inputModalities?.includes('IMAGE')) {
        caps.inputModalities = [...(caps.inputModalities ?? []), 'IMAGE'];
    }
    if (caps.responseStreamingSupported === undefined && taskModalities?.output.includes('TEXT')) {
        const config = offering.providerSpecific?.deploymentConfig;
        if (config?.parameters?.some((parameter) => parameter.name === 'stream')) {
            caps.responseStreamingSupported = true;
        }
    }
    return caps;
}
/** Feature flags published by the provider catalogue (function calling, reasoning, LoRA). */
export function getOfferingFeatureFlags(offering) {
    const properties = providerProperties(offering);
    const flags = [];
    if (truthy(properties.function_calling))
        flags.push('function-calling');
    if (truthy(properties.reasoning))
        flags.push('reasoning');
    if (truthy(properties.vision))
        flags.push('vision');
    if (truthy(properties.lora))
        flags.push('lora');
    return flags;
}
/** Token pricing published with the offering (USD per 1M tokens), when present. */
export function getOfferingTokenPricing(offering) {
    const pricing = offering.providerSpecific?.tokenPricing;
    if (!pricing || typeof pricing.inputTokensPer1M !== 'number')
        return undefined;
    const fetchedAt = offering.providerSpecific?.fetchedAt;
    return {
        input: pricing.inputTokensPer1M,
        output: typeof pricing.outputTokensPer1M === 'number'
            ? pricing.outputTokensPer1M
            : pricing.inputTokensPer1M,
        currency: pricing.currency || 'USD',
        asOf: typeof fetchedAt === 'string' ? fetchedAt : undefined,
    };
}
function isEditorialSource(offering) {
    return offering.metadataSource === 'curated' || offering.metadataSource === 'llm-assisted';
}
function pickDescription(offerings) {
    const usable = offerings.filter((o) => !isGeneratedModelDescription(o.description));
    return (usable.find(isEditorialSource) ?? usable[0])?.description;
}
function pickShortDescription(offerings) {
    const editorial = offerings.find((o) => isEditorialSource(o) && o.shortDescription);
    if (editorial)
        return editorial.shortDescription;
    return offerings.find((o) => !isEditorialSource(o) &&
        o.metadataSource !== 'generated' &&
        o.shortDescription &&
        !isGeneratedModelDescription(o.shortDescription))?.shortDescription;
}
function listJoin(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
/** Plain factual summary for models without provider or editorial copy. */
export function fallbackModelDescription(model) {
    const platforms = Array.from(new Set(model.deployments.map((d) => d.cloudProvider)));
    const list = platforms.length > 0 ? listJoin(platforms) : 'supported inference providers';
    return `${model.displayName} is a ${model.provider} model available through ${list}.`;
}
/**
 * Build the canonical identity index for a set of offerings. The build scripts
 * use the same index, so slugs and names match the prerendered pages.
 */
export function buildCatalogModelIdentityIndex(offerings) {
    return buildModelIdentityIndex(offerings
        .filter((o) => o.status !== 'REMOVED' && o.model?.modelId)
        .map((o) => ({ modelId: o.model.modelId, publisher: o.model.provider })));
}
function toDeployment(offering) {
    const destinationRegions = offering.deployment.destinationRegions ?? [];
    const tokenPricing = getOfferingTokenPricing(offering);
    const residency = offering.residency?.boundary !== undefined
        ? offering.residency
        : withResidencyBoundary(offering);
    return {
        offeringId: offering.offeringId,
        modelId: offering.model.modelId,
        modelName: offering.model.modelName,
        cloudProvider: offering.deployment.cloudProvider,
        deploymentType: offering.deployment.deploymentType,
        sourceRegion: offering.deployment.sourceRegion,
        regions: destinationRegions.length > 0
            ? destinationRegions
            : offering.deployment.sourceRegion
                ? [offering.deployment.sourceRegion]
                : [],
        regionalGroup: offering.deployment.regionalGroup,
        pricingModelName: offering.pricingModelName || offering.model.modelId,
        inferenceOptions: offering.inferenceOptions,
        ...(tokenPricing ? { pricing: tokenPricing } : {}),
        residency: (offering.residency?.scope || 'global'),
        residencyJurisdictions: residency?.jurisdictions,
        residencyBoundary: residency?.boundary,
        staysWithinJurisdiction: residency?.staysWithinJurisdiction,
        regionGroups: residency?.regionGroups,
        status: offering.status,
        capabilities: getOfferingCapabilities(offering),
        variants: modelVariantTags(offering.model.modelId),
        features: getOfferingFeatureFlags(offering),
    };
}
/**
 * Group offerings into catalogue models with deployments. Offerings of the same
 * model from different providers (e.g. Llama 3.1 8B Instruct on Bedrock and its
 * FP8 build on Workers AI) become one model; quantisation is kept per deployment.
 */
export function groupIntoCatalogModels(offerings) {
    const activeOfferings = offerings.filter((o) => o.status !== 'REMOVED' && o.model?.modelId);
    const index = buildCatalogModelIdentityIndex(activeOfferings);
    const modelMap = new Map();
    const membersByKey = new Map();
    for (const offering of activeOfferings) {
        const group = index.forModel(offering.model.modelId, offering.model.provider);
        if (!group)
            continue;
        const mapKey = group.key;
        membersByKey.set(mapKey, [...(membersByKey.get(mapKey) ?? []), offering]);
        const deployment = toDeployment(offering);
        const caps = deployment.capabilities;
        const existing = modelMap.get(mapKey);
        if (existing) {
            existing.deployments.push(deployment);
            if (caps.contextWindowTokens && caps.contextWindowTokens > existing.capabilities.contextWindow) {
                existing.capabilities.contextWindow = caps.contextWindowTokens;
            }
            if (caps.maxOutputTokens &&
                caps.maxOutputTokens > (existing.capabilities.maxOutputTokens || 0)) {
                existing.capabilities.maxOutputTokens = caps.maxOutputTokens;
            }
            if (caps.responseStreamingSupported)
                existing.capabilities.streaming = true;
            for (const modality of caps.inputModalities ?? []) {
                if (!existing.capabilities.inputModalities.includes(modality)) {
                    existing.capabilities.inputModalities.push(modality);
                }
            }
            for (const modality of caps.outputModalities ?? []) {
                if (!existing.capabilities.outputModalities.includes(modality)) {
                    existing.capabilities.outputModalities.push(modality);
                }
            }
            if (offering.createdAt < existing.firstCreated)
                existing.firstCreated = offering.createdAt;
            if (offering.lastUpdated > existing.lastUpdated)
                existing.lastUpdated = offering.lastUpdated;
            if (!existing.enrichment && offering.enrichment)
                existing.enrichment = offering.enrichment;
            continue;
        }
        modelMap.set(mapKey, {
            slug: group.slug,
            aliases: group.aliases,
            variants: group.variants,
            displayName: group.displayName,
            provider: offering.model.provider,
            capabilities: {
                contextWindow: caps.contextWindowTokens || 0,
                maxOutputTokens: caps.maxOutputTokens,
                inputModalities: [...(caps.inputModalities || [])],
                outputModalities: [...(caps.outputModalities || [])],
                streaming: caps.responseStreamingSupported || false,
            },
            deployments: [deployment],
            firstCreated: offering.createdAt,
            lastUpdated: offering.lastUpdated,
            modelCardUrl: offering.modelCardUrl,
            useCases: offering.useCases,
            strengths: offering.strengths,
            bestFor: offering.bestFor,
            releaseDate: offering.releaseDate,
            versionHighlights: offering.versionHighlights,
            benchmarks: offering.benchmarks,
            providerNotes: offering.providerNotes,
            metadataSource: offering.metadataSource,
            enrichment: offering.enrichment,
        });
    }
    for (const [key, model] of modelMap) {
        const members = membersByKey.get(key) ?? [];
        // Prefer editorial metadata from any provider's offering of this model.
        const editorial = members.find(isEditorialSource);
        if (editorial) {
            model.provider = editorial.model.provider;
            model.modelCardUrl = editorial.modelCardUrl ?? model.modelCardUrl;
            model.useCases = editorial.useCases;
            model.strengths = editorial.strengths;
            model.bestFor = editorial.bestFor;
            model.releaseDate = editorial.releaseDate ?? model.releaseDate;
            model.versionHighlights = editorial.versionHighlights;
            model.benchmarks = editorial.benchmarks;
            model.providerNotes = editorial.providerNotes;
            model.metadataSource = editorial.metadataSource;
            model.enrichment = editorial.enrichment ?? model.enrichment;
        }
        else if (model.metadataSource === 'generated') {
            // Generated use cases and strengths are templates, not model facts.
            model.useCases = undefined;
            model.strengths = undefined;
            model.bestFor = undefined;
            model.versionHighlights = undefined;
            model.benchmarks = undefined;
        }
        model.description = pickDescription(members) ?? fallbackModelDescription(model);
        model.shortDescription = pickShortDescription(members);
        model.features = Array.from(new Set(model.deployments.flatMap((d) => d.features ?? [])));
    }
    return Array.from(modelMap.values()).sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime() ||
        a.slug.localeCompare(b.slug));
}
/** Find a model by canonical slug, alias, legacy slug or raw provider model ID. */
export function findCatalogModel(models, key) {
    const candidates = [key];
    try {
        const decoded = decodeURIComponent(key);
        if (decoded !== key)
            candidates.push(decoded);
    }
    catch {
        // Invalid URL encoding: use the raw value only.
    }
    for (const candidate of candidates) {
        const slug = modelPathSegmentToSlug(candidate);
        const found = models.find((m) => m.slug === candidate || m.slug === slug) ??
            models.find((m) => m.aliases?.includes(candidate) || m.aliases?.includes(slug)) ??
            models.find((m) => m.deployments.some((d) => d.modelId === candidate));
        if (found)
            return found;
    }
    return undefined;
}
/** Distinct deployment regions across every active offering in a catalogue. */
export function countCatalogRegions(offerings) {
    const regions = new Set();
    for (const offering of offerings) {
        if (offering.status === 'REMOVED')
            continue;
        for (const region of offering.deployment?.destinationRegions ?? []) {
            if (region)
                regions.add(region);
        }
        if (offering.deployment?.sourceRegion)
            regions.add(offering.deployment.sourceRegion);
    }
    return regions.size;
}
//# sourceMappingURL=catalogModels.js.map